import { Tooltip, makeStyles, tokens, mergeClasses } from '@fluentui/react-components';
import { getRoleDef } from '../../theme/roleDefs';
import { useSemanticColors } from '../../theme/semanticColors';
import { useStaffRoles } from '../../hooks/useStaffRoles';

/**
 * Staff role badges (supervision revamp Task 15).
 *
 * `StaffBadges` renders one compact chip per known role from a `StaffProfile.Roles`
 * csv (already split to string[] by the API). Two densities:
 *  - `size="table"` (default) — icon-only chips, the role label carried as the
 *    accessible name via a Fluent Tooltip (`relationship="label"`). Best for
 *    dense table rows.
 *  - `size="header"` — icon + visible text label, for page headers / cards.
 *
 * Colors come from the calibrated per-theme semantic map (`useSemanticColors`),
 * so chips are theme-aware (light/dark) with no hardcoded hex here. Unknown role
 * tokens are skipped.
 */

export type StaffBadgeSize = 'table' | 'header';

export interface StaffBadgesProps {
  /** Role tokens (e.g. from `StaffProfile.roles`). Null/empty renders nothing. */
  roles: string[] | null | undefined;
  /** Density: icon-only+tooltip for tables (default) or icon+label for headers. */
  size?: StaffBadgeSize;
}

const useStyles = makeStyles({
  group: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalXXS,
    borderRadius: tokens.borderRadiusCircular,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: 'nowrap',
  },
  chipTable: {
    width: '20px',
    height: '20px',
  },
  chipHeader: {
    height: '20px',
    padding: `0 ${tokens.spacingHorizontalSNudge}`,
  },
  icon: {
    fontSize: '14px',
    display: 'inline-flex',
  },
});

export function StaffBadges({ roles, size = 'table' }: StaffBadgesProps) {
  const styles = useStyles();
  const semantic = useSemanticColors();

  const defs = (roles ?? [])
    .map((role) => ({ role, def: getRoleDef(role) }))
    .filter((x): x is { role: string; def: NonNullable<ReturnType<typeof getRoleDef>> } =>
      x.def !== undefined,
    );

  if (defs.length === 0) return null;

  return (
    <span className={styles.group}>
      {defs.map(({ role, def }) => {
        const color = semantic[def.color];
        const Icon = def.icon;
        const chip = (
          <span
            className={mergeClasses(
              styles.chip,
              size === 'table' ? styles.chipTable : styles.chipHeader,
            )}
            style={{ backgroundColor: color.fill, color: color.onFill }}
          >
            <Icon className={styles.icon} />
            {size === 'header' ? def.label : null}
          </span>
        );

        // Table density: label is the accessible name via Tooltip (no separate
        // aria-label/title — avoids the Fluent controlled-widget hang gotcha).
        if (size === 'table') {
          return (
            <Tooltip key={role} content={def.label} relationship="label" withArrow>
              {chip}
            </Tooltip>
          );
        }

        return <span key={role}>{chip}</span>;
      })}
    </span>
  );
}

export interface StaffRoleBadgesProps {
  /**
   * Any staff identifier a surface has on hand — a Dataverse `systemuserid`
   * (action-item / meeting `bookkeeperId`), a display name (`ClientDetailPage`
   * owner), or a core `StaffId`. Matched liberally against the staff roster.
   */
  staffIdentifier: string | null | undefined;
  size?: StaffBadgeSize;
}

/**
 * Convenience wrapper: resolves a staff member's roles from the roster and
 * renders {@link StaffBadges}. Safe to drop inside a table-row `.map()` — each
 * instance is its own component, so the underlying `useStaffRoles` hook runs
 * per-instance (no hook-in-loop). Renders nothing when the roster is
 * unavailable or nothing matches.
 */
export function StaffRoleBadges({ staffIdentifier, size }: StaffRoleBadgesProps) {
  const roles = useStaffRoles(staffIdentifier);
  return <StaffBadges roles={roles} size={size} />;
}

export default StaffBadges;
